import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Circle, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ChecklistItemStatus = 'pending' | 'confirmed' | 'not_confirmed';

interface OnboardingChecklistItemProps {
  index: number;
  label: string;
  script?: string;
  status: ChecklistItemStatus;
  notes?: string;
  required: boolean;
  onConfirm: () => void;
  onReject: (notes: string) => void;
  onReset: () => void;
  extraContent?: React.ReactNode;
}

export default function OnboardingChecklistItem({
  index,
  label,
  script,
  status,
  notes,
  required,
  onConfirm,
  onReject,
  onReset,
  extraContent,
}: OnboardingChecklistItemProps) {
  const [rejectionNotes, setRejectionNotes] = useState(notes || '');
  const [showNotesInput, setShowNotesInput] = useState(status === 'not_confirmed');

  const handleReject = () => {
    if (status === 'not_confirmed') {
      // Already rejected, just update notes
      onReject(rejectionNotes);
    } else {
      setShowNotesInput(true);
    }
  };

  const handleConfirmRejection = () => {
    onReject(rejectionNotes);
    setShowNotesInput(false);
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle2 className="h-6 w-6 text-emerald-500" />;
      case 'not_confirmed':
        return <XCircle className="h-6 w-6 text-destructive" />;
      default:
        return <Circle className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getStatusBgClass = () => {
    switch (status) {
      case 'confirmed':
        return 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30';
      case 'not_confirmed':
        return 'border-destructive/30 bg-destructive/5';
      default:
        return 'border-border bg-card';
    }
  };

  return (
    <Card className={cn('transition-all', getStatusBgClass())}>
      <CardContent className="p-4 space-y-3">
        {/* Header with status icon and label */}
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{getStatusIcon()}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">
                {index + 1}. {label}
              </span>
              {required && (
                <span className="text-xs text-destructive font-medium">*</span>
              )}
            </div>
          </div>
          {status !== 'pending' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              title="Resetar"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Script/Instructions */}
        {script && (
          <div className="ml-9 p-3 rounded-lg bg-muted/50 border border-border">
            <p className="text-sm text-muted-foreground italic whitespace-pre-wrap">
              "{script}"
            </p>
          </div>
        )}

        {/* Extra Content (contracts, documents, etc.) */}
        {extraContent && (
          <div className="ml-9">
            {extraContent}
          </div>
        )}

        {/* Action buttons */}
        {status === 'pending' && !showNotesInput && (
          <div className="ml-9 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onConfirm}
              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-800 dark:hover:bg-emerald-950"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Confirmado
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReject}
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Não confirmou
            </Button>
          </div>
        )}

        {/* Notes input for rejection */}
        {showNotesInput && status === 'pending' && (
          <div className="ml-9 space-y-2">
            <Textarea
              placeholder="Motivo ou observação..."
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              className="min-h-[80px]"
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleConfirmRejection}
              >
                Confirmar não atendeu
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowNotesInput(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Show notes if rejected */}
        {status === 'not_confirmed' && notes && (
          <div className="ml-9 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive font-medium mb-1">Motivo:</p>
            <p className="text-sm text-foreground">{notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
